import type { ComboboxRootChangeEventDetails } from '@base-ui/react/combobox'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ComboboxModeProps, MultipleProps, SharedProps } from './types'

export type MultiComboboxStateProps<T> = SharedProps<T> &
  ComboboxModeProps<T> &
  Omit<MultipleProps<T>, 'multiple'>

const USER_INPUT_REASONS: ComboboxRootChangeEventDetails['reason'][] = [
  'input-change',
  'input-clear',
]

export function useMultiComboboxState<T>(props: MultiComboboxStateProps<T>) {
  const {
    options,
    getOptionLabel,
    getOptionValue: getOptionValueProp,
    onInputValueChange,
    value: multiValue,
    onChange,
    onCreate,
    isValidNewOption,
    onKeyDown,
  } = props

  const getOptionValue = getOptionValueProp ?? getOptionLabel

  const [inputValue, setInputValue] = useState('')

  const handleInputChange = useCallback(
    (val: string, { reason }: ComboboxRootChangeEventDetails) => {
      setInputValue(val)
      if (!USER_INPUT_REASONS.includes(reason)) return
      onInputValueChange?.(val)
    },
    [onInputValueChange],
  )

  const handleValueChange = useCallback(
    (next: T[]) => onChange(next),
    [onChange],
  )

  const handleClear = useCallback(() => {
    setInputValue('')
    onInputValueChange?.('')
    onChange([])
  }, [onChange, onInputValueChange])

  const selectedKeys = useMemo(
    () => new Set(multiValue.map(v => getOptionValue(v))),
    [multiValue, getOptionValue],
  )

  const showCreate = useMemo(
    () =>
      !!onCreate &&
      !!inputValue &&
      (!isValidNewOption || isValidNewOption(inputValue)) &&
      !options.some(
        o => getOptionLabel(o).toLowerCase() === inputValue.toLowerCase(),
      ),
    [onCreate, inputValue, isValidNewOption, options, getOptionLabel],
  )

  // Snapshot onCreate so handleCreate stays referentially stable while still
  // calling the latest onCreate. Avoids re-running effects in callers that
  // depend on the handler.
  const onCreateRef = useRef(onCreate)
  useEffect(() => {
    onCreateRef.current = onCreate
  })

  const handleCreate = useCallback(() => {
    if (!inputValue) return
    onCreateRef.current?.(inputValue)
    setInputValue('')
  }, [inputValue])

  return {
    items: options,
    inputValue,
    multiValue,
    selectedKeys,
    showCreate,
    onKeyDown,
    getOptionValue,
    handleInputChange,
    handleValueChange,
    handleClear,
    handleCreate,
  }
}
